import React from "react";
import { ArrowLeft, Copy, Check } from "lucide-react";
import { useState } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { AI_ASSISTANT_RICH_CONTENT_TAGS } from "@/config/aiAssistantRichContent";
import {
  AIInfoBox,
  AIHighlight,
  AIActionItem,
  AIWarning,
  AICodeBlock,
  AIQuote,
  AICallout,
  AITask,
  AIChecklistItem,
  AIColorPreview,
  AITable,
  AIUnorderedList,
  AIOrderedList,
} from "@/components/ai-assistant/AIRichContent";

const AIAssistantFormattingPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [copiedTag, setCopiedTag] = useState<string | null>(null);

  const copyToClipboard = async (text: string, tagName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedTag(tagName);
      toast({
        title: "Copied!",
        description: `${tagName} tag copied to clipboard`,
      });
      setTimeout(() => setCopiedTag(null), 2000);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  const aiInstructionPrompt = `When generating content, use these formatting options to enhance readability and engagement:

## Rich Content Tags:
- <info-box>content</info-box> - Important information, key points, highlights
- <highlight>text</highlight> - Emphasize keywords, offers, important terms  
- <action-item>content</action-item> - Clear next steps, CTAs, recommendations
- <warning>content</warning> - Time-sensitive info, limitations, urgent notices
- <code-block>code</code-block> - Code examples, technical snippets
- <quote>content</quote> - Testimonials, inspirational quotes, citations
- <callout>content</callout> - Pro tips, insights, bonus information
- <task>content</task> - Interactive task items with checkboxes

## Standard Formatting:
- **bold text** for important words
- *italic text* for emphasis
- # Main Heading
- ## Sub Heading  
- ### Section Heading
- <ul>item1|item2|item3</ul> for bullet point lists  
- <ol>step1|step2|step3</ol> for numbered lists
- <checklist>task1|task2|task3</checklist> for checkbox lists
- - [ ] Checklist items (unchecked)
- [Link text](URL) for external links
- #FF5733 color codes (shows color preview)

## Tables:
| Header 1 | Header 2 | Header 3 |
| Cell 1 | Cell 2 | Cell 3 |
| Row 2 | Data | More data |

Use these formats appropriately based on content type and user needs.`;

  const formattingExamples = [
    {
      name: "Info Box",
      example: "<info-box>Important information</info-box>",
      component: (
        <AIInfoBox
          content="Important information"
          className={AI_ASSISTANT_RICH_CONTENT_TAGS.INFO_BOX.className}
        />
      ),
    },
    {
      name: "Highlight",
      example: "<highlight>special offer</highlight>",
      component: (
        <AIHighlight
          content="special offer"
          className={AI_ASSISTANT_RICH_CONTENT_TAGS.HIGHLIGHT.className}
        />
      ),
    },
    {
      name: "Action Item",
      example: "<action-item>Schedule a demo call</action-item>",
      component: (
        <AIActionItem
          content="Schedule a demo call"
          className={AI_ASSISTANT_RICH_CONTENT_TAGS.ACTION_ITEM.className}
        />
      ),
    },
    {
      name: "Warning",
      example: "<warning>Offer expires in 48 hours!</warning>",
      component: (
        <AIWarning
          content="Offer expires in 48 hours!"
          className={AI_ASSISTANT_RICH_CONTENT_TAGS.WARNING.className}
        />
      ),
    },
    {
      name: "Code Block",
      example: "<code-block>const api = 'key';</code-block>",
      component: (
        <AICodeBlock
          content="const api = 'key';"
          className={AI_ASSISTANT_RICH_CONTENT_TAGS.CODE_BLOCK.className}
        />
      ),
    },
    {
      name: "Quote",
      example: "<quote>Success is not final - Churchill</quote>",
      component: (
        <AIQuote
          content="Success is not final - Churchill"
          className={AI_ASSISTANT_RICH_CONTENT_TAGS.QUOTE.className}
        />
      ),
    },
    {
      name: "Callout",
      example: "<callout>Pro Tip: Save 3+ hours per week</callout>",
      component: (
        <AICallout
          content="Pro Tip: Save 3+ hours per week"
          className={AI_ASSISTANT_RICH_CONTENT_TAGS.CALLOUT.className}
        />
      ),
    },
    {
      name: "Task",
      example: "<task>Review and approve the marketing campaign</task>",
      component: (
        <div className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
          <input type="checkbox" className="mt-1" />
          <span>Review and approve the marketing campaign</span>
        </div>
      ),
    },
    {
      name: "Unordered List",
      example: "<ul>First item|Second item|Third item</ul>",
      component: (
        <AIUnorderedList
          content="First item|Second item|Third item"
          className={AI_ASSISTANT_RICH_CONTENT_TAGS.UNORDERED_LIST.className}
        />
      ),
    },
    {
      name: "Ordered List",
      example: "<ol>Step one|Step two|Step three</ol>",
      component: (
        <AIOrderedList
          content="Step one|Step two|Step three"
          className={AI_ASSISTANT_RICH_CONTENT_TAGS.ORDERED_LIST.className}
        />
      ),
    },
    {
      name: "Checklist",
      example: "<checklist>Task one|Task two|Task three</checklist>",
      component: (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input type="checkbox" className="rounded" />
            <span>Task one</span>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" className="rounded" />
            <span>Task two</span>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" className="rounded" />
            <span>Task three</span>
          </div>
        </div>
      ),
    },
  ];

  return (
    <MainLayout>
      <SuperAdminLayout
        title="AI Assistant Formatting Tags"
        description="Quick reference for rich content formatting options"
        actions={
          <Button
            variant="outline"
            onClick={() => navigate("/super-admin/ai-assistants")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to AI Assistants
          </Button>
        }
      >
        {/* AI Instruction Prompt */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              🤖 AI Formatting Instructions
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  copyToClipboard(aiInstructionPrompt, "AI Instructions")
                }
                className="h-8 px-2"
              >
                {copiedTag === "AI Instructions" ? (
                  <Check className="h-3 w-3 text-green-600" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
                Copy Prompt
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 rounded-lg p-4 font-mono text-xs whitespace-pre-wrap overflow-x-auto">
              {aiInstructionPrompt}
            </div>
            <p className="text-sm text-gray-600 mt-3">
              Copy this prompt and paste it into your AI assistant instructions
              to ensure proper formatting usage.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Available Formatting Tags & Features</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {formattingExamples.map((example) => (
                <div key={example.name} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">{example.name}</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        copyToClipboard(example.example, example.name)
                      }
                      className="h-8 px-2"
                    >
                      {copiedTag === example.name ? (
                        <Check className="h-3 w-3 text-green-600" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>

                  <div className="bg-gray-100 rounded p-2 font-mono text-xs">
                    {example.example}
                  </div>

                  <div className="border rounded p-3 bg-white">
                    {example.component}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t space-y-6">
              <div>
                <h4 className="font-medium mb-3">Basic Formatting</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-mono">**bold**</span>
                    <span className="font-bold">bold</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono">*italic*</span>
                    <span className="italic">italic</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono">[link](url)</span>
                    <span className="text-blue-600 underline">link</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono">#FF5733</span>
                    <div className="flex items-center gap-2">
                      <span className="w-4 h-4 bg-red-500 rounded border"></span>
                      <span className="font-mono text-xs">#FF5733</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-3">Headings & Lists</h4>
                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="font-mono mb-1"># Main Heading</div>
                      <h1 className="text-xl font-bold">Main Heading</h1>
                    </div>
                    <div>
                      <div className="font-mono mb-1">## Sub Heading</div>
                      <h2 className="text-lg font-semibold">Sub Heading</h2>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="font-mono mb-1">- Bullet point</div>
                      <ul className="list-disc list-inside">
                        <li>Bullet point</li>
                      </ul>
                    </div>
                    <div>
                      <div className="font-mono mb-1">1. Numbered item</div>
                      <ol className="list-decimal list-inside">
                        <li>Numbered item</li>
                      </ol>
                    </div>
                  </div>
                  <div>
                    <div className="font-mono mb-1">- [ ] Checklist item</div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" className="rounded" />
                      <span>Checklist item</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-3">Tables</h4>
                <div className="space-y-3">
                  <div className="font-mono text-xs bg-gray-100 p-2 rounded">
                    | Header 1 | Header 2 | Header 3 |<br />
                    | Cell 1 | Cell 2 | Cell 3 |<br />| Row 2 | Data | More data
                    |
                  </div>
                  <div className="border rounded overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left border-b">
                            Header 1
                          </th>
                          <th className="px-3 py-2 text-left border-b">
                            Header 2
                          </th>
                          <th className="px-3 py-2 text-left border-b">
                            Header 3
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="px-3 py-2 border-b">Cell 1</td>
                          <td className="px-3 py-2 border-b">Cell 2</td>
                          <td className="px-3 py-2 border-b">Cell 3</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="px-3 py-2">Row 2</td>
                          <td className="px-3 py-2">Data</td>
                          <td className="px-3 py-2">More data</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </SuperAdminLayout>
    </MainLayout>
  );
};

export default AIAssistantFormattingPage;
